import PubSub from "pubsub-js"

const observeImageRemoval = (
  selectedImage,
  removeExistingGizmo,
  editorContainer,
  handleScroll
) => {
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      // Check if the selected image is removed
      if (mutation.type === "childList") {
        mutation.removedNodes.forEach((removedNode) => {
          if (removedNode === selectedImage) {
            removeExistingGizmo()
            editorContainer.removeEventListener("scroll", handleScroll)
            observer.disconnect() // Stop observing after the image is removed
          }
        })
      }
    }
  })

  // Start observing the parent node of the selected image
  if (selectedImage.parentNode) {
    observer.observe(selectedImage.parentNode, { childList: true })
  }
}

const observeImageChanges = (selectedImage, gizmo) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(() => {
      updateGizmoPosition(gizmo, selectedImage)
    })
  })

  // Watch both the image and its figure container (if it exists)
  const transformTarget = getTransformTarget(selectedImage)
  
  observer.observe(selectedImage, {
    attributes: true,
    attributeFilter: ["style", "class"], // Only watch for style and class changes
    childList: false,
    subtree: false,
  })
  
  // If image is in a figure, also observe the figure
  if (transformTarget !== selectedImage) {
    observer.observe(transformTarget, {
      attributes: true,
      attributeFilter: ["style", "class"],
      childList: false,
      subtree: false,
    })
  }

  return observer
}

const TransformGizmo = (selectedImage) => {
  if (!selectedImage) return

  // Remove any existing gizmos first
  removeExistingGizmo()

  // Create gizmo container
  const gizmo = document.createElement("div")
  gizmo.className =
    "ae-transform-gizmo absolute border-2 border-admin_primary pointer-events-none"
  gizmo.style.position = "absolute"
  gizmo.style.zIndex = "900"
  gizmo.style.pointerEvents = "none"

  // Add resize handles to corners and edges
  const handlePositions = [
    "top-left",
    "top",
    "top-right",
    "left",
    "right",
    "bottom-left",
    "bottom",
    "bottom-right",
  ]

  handlePositions.forEach((position) => {
    const handle = document.createElement("div")
    handle.className = `
      absolute w-3 h-3 bg-admin_paper border-2 border-admin_primary rounded-sm
      cursor-${getCursorStyle(position)} pointer-events-auto
    `

    // Position the handle
    switch (position) {
      case "top-left":
        handle.style.top = "-6px"
        handle.style.left = "-6px"
        break
      case "top":
        handle.style.top = "-6px"
        handle.style.left = "50%"
        handle.style.transform = "translateX(-50%)"
        break
      case "top-right":
        handle.style.top = "-6px"
        handle.style.right = "-6px"
        break
      case "left":
        handle.style.left = "-6px"
        handle.style.top = "50%"
        handle.style.transform = "translateY(-50%)"
        break
      case "right":
        handle.style.right = "-6px"
        handle.style.top = "50%"
        handle.style.transform = "translateY(-50%)"
        break
      case "bottom-left":
        handle.style.bottom = "-6px"
        handle.style.left = "-6px"
        break
      case "bottom":
        handle.style.bottom = "-6px"
        handle.style.left = "50%"
        handle.style.transform = "translateX(-50%)"
        break
      case "bottom-right":
        handle.style.bottom = "-6px"
        handle.style.right = "-6px"
        break
    }

    handle.addEventListener("mousedown", (e) => {
      e.stopPropagation()
      initResize(e, selectedImage, position)
    })

    gizmo.appendChild(handle)
  })

  const editorContainer = document.querySelector(".editor")

  // Add scroll event listener
  const handleScroll = () => {
    requestAnimationFrame(() => {
      updateGizmoPosition(gizmo, selectedImage)
    })
  }
  editorContainer.addEventListener("scroll", handleScroll)

  // Set up mutation observers
  const styleObserver = observeImageChanges(selectedImage, gizmo)
  const removalObserver = observeImageRemoval(
    selectedImage,
    () => {
      removeExistingGizmo()
      styleObserver.disconnect()
    },
    editorContainer,
    handleScroll
  )

  // Initial position
  updateGizmoPosition(gizmo, selectedImage)

  // Add gizmo to editor container
  editorContainer.appendChild(gizmo)

  // Handle clicks outside
  const handleClickOutside = (e) => {
    // Get the transform target (figure container or image)
    const transformTarget = getTransformTarget(selectedImage)
    
    // Don't remove gizmo if clicking on:
    // 1. The gizmo itself
    // 2. The selected image
    // 3. The figure container (if image is in one)
    // 4. The horizontal options menu
    // 5. Any dropdown panels (portals)
    // 6. Any tooltips
    
    if (gizmo.contains(e.target)) return
    if (e.target === selectedImage) return
    if (e.target === transformTarget) return
    
    // Check if click is inside horizontal options menu
    const horizontalMenu = document.querySelector('.ae-horizontal-options-menu-container')
    if (horizontalMenu && horizontalMenu.contains(e.target)) return
    
    // Check if click is inside any dropdown panels
    const dropdownPanels = document.querySelectorAll('[role="menu"], .ae-dropdown-panel')
    for (let panel of dropdownPanels) {
      if (panel.contains(e.target)) return
    }
    
    // Check if click is inside any tooltip
    const tooltips = document.querySelectorAll('[class*="tooltip"], [data-tooltip]')
    for (let tooltip of tooltips) {
      if (tooltip.contains(e.target)) return
    }
    
    // If we get here, it's a click outside - remove the gizmo
    removeExistingGizmo()
    document.removeEventListener("mousedown", handleClickOutside)
    editorContainer.removeEventListener("scroll", handleScroll)
    styleObserver && styleObserver.disconnect()
    removalObserver && removalObserver.disconnect()
  }

  document.addEventListener("mousedown", handleClickOutside)

  // Set up resize observer for the editor container
  const resizeObserver = new ResizeObserver(() => {
    requestAnimationFrame(() => {
      updateGizmoPosition(gizmo, selectedImage)
    })
  })

  resizeObserver.observe(editorContainer)

  // Subscribe to content updates to refresh gizmo position
  // This handles cases where CSS changes (borders, padding, etc.) affect element dimensions
  const contentUpdateToken = PubSub.subscribe("html-editor:content-update", () => {
    // Use requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      updateGizmoPosition(gizmo, selectedImage)
    })
  })

  // Return cleanup function
  return () => {
    removeExistingGizmo()
    document.removeEventListener("mousedown", handleClickOutside)
    editorContainer.removeEventListener("scroll", handleScroll)
    styleObserver.disconnect()
    removalObserver.disconnect()
    resizeObserver.disconnect()
    PubSub.unsubscribe(contentUpdateToken)
  }
}

// Helper function to get appropriate cursor style
const getCursorStyle = (position) => {
  switch (position) {
    case "top-left":
    case "bottom-right":
      return "nw-resize"
    case "top-right":
    case "bottom-left":
      return "ne-resize"
    case "top":
    case "bottom":
      return "ns-resize"
    case "left":
    case "right":
      return "ew-resize"
    default:
      return "move"
  }
}

// Helper function to remove existing gizmo
const removeExistingGizmo = () => {
  const existingGizmo = document.querySelector(".ae-transform-gizmo")
  if (existingGizmo) {
    existingGizmo.remove()
  }
}

// Helper function to get the transform target (figure container or image)
const getTransformTarget = (image) => {
  const figureContainer = image.closest("figure")
  return figureContainer || image
}

// Helper function to update gizmo position
const updateGizmoPosition = (gizmo, image) => {
  const editorContainer = document.querySelector(".editor")
  if (!editorContainer) return
  
  // Get the actual element we should position around (figure or image)
  const transformTarget = getTransformTarget(image)
  const editorRect = editorContainer.getBoundingClientRect()
  const targetRect = transformTarget.getBoundingClientRect()

  // Calculate position relative to the editor container
  const top = targetRect.top - editorRect.top + editorContainer.scrollTop
  const left = targetRect.left - editorRect.left + editorContainer.scrollLeft

  gizmo.style.left = `${left}px`
  gizmo.style.top = `${top}px`
  gizmo.style.width = `${targetRect.width}px`
  gizmo.style.height = `${targetRect.height}px`
}

// Initialize resize functionality
const initResize = (e, image, position) => {
  e.preventDefault()
  const startX = e.clientX
  const startY = e.clientY
  
  // Get the transform target (figure or image)
  const transformTarget = getTransformTarget(image)
  const startWidth = transformTarget.offsetWidth
  const startHeight = transformTarget.offsetHeight

  const onMouseMove = (moveEvent) => {
    moveEvent.preventDefault()
    const deltaX = moveEvent.clientX - startX
    const deltaY = moveEvent.clientY - startY
    let newWidth = startWidth
    let newHeight = startHeight

    // Calculate new dimensions based on handle position
    switch (position) {
      case "right":
        newWidth = Math.max(50, startWidth + deltaX)
        break
      case "left":
        newWidth = Math.max(50, startWidth - deltaX)
        break
      case "bottom":
        newHeight = Math.max(50, startHeight + deltaY)
        break
      case "top":
        newHeight = Math.max(50, startHeight - deltaY)
        break
      case "bottom-right":
        newWidth = Math.max(50, startWidth + deltaX)
        newHeight = Math.max(50, startHeight + deltaY)
        break
      case "bottom-left":
        newWidth = Math.max(50, startWidth - deltaX)
        newHeight = Math.max(50, startHeight + deltaY)
        break
      case "top-right":
        newWidth = Math.max(50, startWidth + deltaX)
        newHeight = Math.max(50, startHeight - deltaY)
        break
      case "top-left":
        newWidth = Math.max(50, startWidth - deltaX)
        newHeight = Math.max(50, startHeight - deltaY)
        break
    }

    // Apply size changes to the transform target (figure or image)
    const figureContainer = image.closest("figure")
    if (figureContainer) {
      // For images in figure containers, apply size to both figure and image
      figureContainer.style.width = `${Math.round(newWidth)}px`
      image.style.width = `${Math.round(newWidth)}px`
      image.style.height = `${Math.round(newHeight)}px`
      
      // Remove any existing width classes from both elements
      const figureWidthClass = Array.from(figureContainer.classList).find((cls) =>
        cls.startsWith("w-")
      )
      if (figureWidthClass) {
        figureContainer.classList.remove(figureWidthClass)
      }
    } else {
      // For standalone images, apply directly to image
      image.style.width = `${Math.round(newWidth)}px`
      image.style.height = `${Math.round(newHeight)}px`
    }

    // Remove any existing width classes from image
    const currentWidthClass = Array.from(image.classList).find((cls) =>
      cls.startsWith("w-")
    )
    if (currentWidthClass) {
      image.classList.remove(currentWidthClass)
    }

    // Update gizmo position
    const gizmo = document.querySelector(".ae-transform-gizmo")
    if (gizmo) {
      updateGizmoPosition(gizmo, image)
    }
  }

  const onMouseUp = () => {
    document.removeEventListener("mousemove", onMouseMove)
    document.removeEventListener("mouseup", onMouseUp)
  }

  document.addEventListener("mousemove", onMouseMove)
  document.addEventListener("mouseup", onMouseUp)
}

export default TransformGizmo
export { removeExistingGizmo }
